/**
 * Kennel / yard-worker operational home (#134).
 *
 * On-site roster for kennel_tech role:
 *  - Today's checked-in dogs with run assignment + vax status
 *  - Log meals WITH AN OUTCOME (#721) — ate all / ate some / refused
 *  - Assign / update run slot
 *
 * #721 — the meal buttons used to be one tap that recorded only "a meal was
 * logged". They are now two: pick the meal, then say what happened. That
 * second tap is the entire feature — "hasn't eaten dinner the last 2
 * nights" is unanswerable without it — and the refusal streak it produces
 * renders on each card via CareStreakBadge, computed server-side with the
 * roster so a 30-dog morning is still one request.
 *
 * Sawyer 02 kennel-stopgap (A1-stopgap, A3-stopgap) — the same page is the desk's "Meds &
 * feeding" list. One component, two framings chosen by PRIMARY role (the
 * #428 rule WelcomeCard and personaHomeRoute use):
 *  - floor (primary kennel_tech): "Kennel Roster", Welcome + Time Clock cards,
 *    no back link — the kennel tech's home, unchanged apart from the care
 *    lines.
 *  - desk (owner / manager / receptionist): "Meds & feeding", a back link to
 *    where they came from (`?from=day-list|dashboard`), no persona cards (so
 *    their fetches never fire).
 * Both read the date from `?date=` and print each dog's PROFILE feeding and
 * medication text, labelled as such: a stay's run-card edits are not on this
 * read (lane 08's report reads them), and the caption says so (Principle 5).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { PageHeader } from '@ferro/ui';
import {
  kennelApi,
  todayIsoDate,
  humanizeApiError,
  type MealOutcome,
  type OnSiteAnimal,
  type FeedingLogEntry,
} from '../lib/api.js';
import { TimeClockCard, type TimeClockStatus } from '../components/timeclock/TimeClockCard.js';
import { WelcomeCard } from '../components/WelcomeCard.js';
import { CareStreakBadge } from '../components/care-log/CareStreakBadge.js';
import { AteNoneBadge, parseAteNone, streakRepeatsAteNone } from '../components/care-log/AteNoneBadge.js';
import { DogFlagChips } from '../components/dog-first/index.js';
import { useAuth } from '../contexts/AuthContext.js';
import { effectiveRolesOf } from '../lib/rbac-matrix.js';
import { rolesCanAccessPath } from '../lib/route-permissions.js';
import {
  dayListHref,
  formatDayLabel,
  isIsoDate,
  parseDayView,
  shiftIsoDate,
} from '../components/dashboard/day-list-filters.js';
import './kennel-home.css';

type KennelHomeProps = { config: ClientConfig };

type MealType = FeedingLogEntry['mealType'];

const MEALS: readonly MealType[] = ['breakfast', 'lunch', 'dinner', 'snack'];

/** Outcome copy for the floor. Never the raw enum (§Anti-patterns). */
const OUTCOME_BUTTONS: ReadonlyArray<{ value: MealOutcome; label: string }> = [
  { value: 'ate_all', label: 'Ate all' },
  { value: 'ate_partial', label: 'Ate some' },
  // Sawyer #1295: the desk's word. The stored value stays `refused`.
  { value: 'refused', label: 'Ate none' },
];

const MEAL_LABEL: Record<MealType, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
};

// Ted #28 Phase 2 (#1050) — the roster's private VaxChip is retired; the
// card draws the ONE registry's flags (vax / INTACT / meds / transport)
// through the one renderer. `vaccineStatus` still drives the header counts.

/** Sawyer 02 kennel-stopgap (A1-stopgap) — the profile text is what HTHD has (Gingr import). */
const SOURCE_NOTE =
  "Feeding and medication notes from each dog's profile. Changes made for this stay are on the run card.";

/** Trimmed text, or null for absent / whitespace-only (never a blank line). */
function careText(raw: string | null | undefined): string | null {
  const t = raw?.trim();
  return t ? t : null;
}

/** "8:02 AM" — the stale-state stamp (Principle 5). */
function formatUpdatedAt(at: Date): string {
  return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Where the desk framing's back link goes, from `?from=` and the Day list filters. */
function backTarget(
  from: string | null,
  date: string,
  service: string | null,
  view: string | null,
): { label: string; href: string } {
  if (from === 'dashboard') return { label: 'Dashboard', href: '/' };
  return {
    label: 'Day list',
    href: dayListHref({ date, serviceTypeId: service, view: parseDayView(view) }),
  };
}

export function KennelHome({ config }: KennelHomeProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const { staff } = useAuth();
  // #428 — the PRIMARY role picks the framing; grants widen reach, not home.
  const isFloorPersona = staff?.role === 'kennel_tech';
  const roles = effectiveRolesOf(staff);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDate = searchParams.get('date');
  const date = isIsoDate(rawDate) ? rawDate : todayIsoDate();
  const [animals, setAnimals] = useState<OnSiteAnimal[]>([]);
  // Sawyer refuter C2 — which date `animals` belongs to. Cards render only
  // for the date on screen, so a date change (or a failed read for the new
  // date) can never show yesterday's dogs and meds under today's heading.
  const [rosterDate, setRosterDate] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const latestRequest = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [feedSuccess, setFeedSuccess] = useState<string | null>(null);
  // #721 — which dog+meal is currently asking "what happened?". One at a
  // time: a card full of open outcome rows is noise, and the floor logs one
  // bowl at a time anyway.
  const [openMeal, setOpenMeal] = useState<string | null>(null);
  const [runEdits, setRunEdits] = useState<Record<string, string>>({});
  // #427 W1a — punch status lifted from the Time Clock card. This
  // roster has NO filled-primary queue actions (Assign = outline,
  // meals = ghost), so demotion only affects the card itself: clocked
  // out ⇒ Clock In is the view's sole primary; clocked in ⇒ the card
  // renders secondary and the roster keeps its own quiet hierarchy
  // (spec W1a / P2).
  const [clockStatus, setClockStatus] = useState<TimeClockStatus>('unknown');

  const load = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setIsLoading(true);
    setError(null);
    const res = await kennelApi.getRoster(apiUrl, date);
    // A slower answer for a date the viewer already left is dropped.
    if (requestId !== latestRequest.current) return;
    setIsLoading(false);
    if (res.error) setError(humanizeApiError(res.error));
    else if (res.data) {
      setAnimals(res.data.animals);
      setRosterDate(date);
      setUpdatedAt(new Date());
    }
  }, [apiUrl, date]);

  useEffect(() => { void load(); }, [load]);

  // Sawyer refuter C3 — a desk tab left open all day re-reads when it comes
  // back into view (the AppShell date chip's #711 mechanism), and the
  // "Updated" stamp says how old the list is.
  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.visibilityState !== 'hidden') void load();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [load]);

  const handleLogFeeding = async (
    animal: OnSiteAnimal,
    mealType: MealType,
    outcome: MealOutcome,
  ): Promise<void> => {
    setPendingId(`${animal.dogId}-${mealType}`);
    setActionError(null);
    setFeedSuccess(null);
    const res = await kennelApi.logFeeding(apiUrl, animal.dogId, mealType, outcome);
    setPendingId(null);
    if (res.error) { setActionError(res.error); return; }
    setOpenMeal(null);
    const outcomeWord =
      OUTCOME_BUTTONS.find((o) => o.value === outcome)?.label.toLowerCase() ??
      outcome;
    setFeedSuccess(`${animal.dogName} — ${MEAL_LABEL[mealType].toLowerCase()}: ${outcomeWord}`);
    setTimeout(() => setFeedSuccess(null), 3000);
    // A refusal can start or extend a streak, and the badge on this card is
    // server-computed — so re-read the roster rather than guessing locally.
    // Two sources that can disagree is the defect (§1 one glance, one truth).
    if (outcome === 'refused') void load();
  };

  const handleAssignRun = async (animal: OnSiteAnimal): Promise<void> => {
    const run = runEdits[animal.bookingId]?.trim();
    if (!run) return;
    setPendingId(`run-${animal.bookingId}`);
    setActionError(null);
    const res = await kennelApi.assignRun(apiUrl, animal.bookingId, run);
    setPendingId(null);
    if (res.error) { setActionError(res.error); return; }
    setAnimals((prev) =>
      prev.map((a) =>
        a.bookingId === animal.bookingId ? { ...a, runAssignment: run } : a,
      ),
    );
    setRunEdits((prev) => { const n = { ...prev }; delete n[animal.bookingId]; return n; });
  };

  // The date lives in the URL (`from`, and the Day list filters the back link
  // rebuilds, ride along); replace, so ‹ › do not stack history entries.
  const setDate = (iso: string): void => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('date', iso);
    setSearchParams(nextParams, { replace: true });
  };
  const prev = (): void => setDate(shiftIsoDate(date, -1));
  const next = (): void => setDate(shiftIsoDate(date, 1));

  // Cards (and every count drawn from them) belong to the date on screen only.
  const hasRoster = rosterDate === date && !error;
  const shown = hasRoster ? animals : [];
  const expiredCount = shown.filter((a) => a.vaccineStatus === 'expired').length;
  const unverifiedCount = shown.filter((a) => a.vaccineStatus === 'unverified').length;
  const dateLabel = formatDayLabel(date);
  const dogsWord = `${shown.length} dog${shown.length !== 1 ? 's' : ''}`;
  // Principle 5 — a loading zero is not a true zero: no count until data lands.
  const subtitle = hasRoster
    ? isFloorPersona
      ? `${dogsWord} on site`
      : `${dogsWord} checked in · daycare and boarding`
    : isLoading
      ? 'Loading…'
      : undefined;
  const back = isFloorPersona
    ? null
    : backTarget(
        searchParams.get('from'),
        date,
        searchParams.get('service'),
        searchParams.get('view'),
      );

  return (
    <section className="ferro-admin-page ferro-role-screen ferro-kennel">
      {back ? (
        <Link className="ferro-back ferro-kennel__back" to={back.href} data-testid="kennel-back">
          ← {back.label}
        </Link>
      ) : null}

      <div className="ferro-kennel__head">
        <PageHeader title={isFloorPersona ? 'Kennel Roster' : 'Meds & feeding'} subtitle={subtitle} />
        {hasRoster && updatedAt ? (
          <p className="ferro-muted ferro-kennel__updated" data-testid="kennel-updated-at">
            Updated {formatUpdatedAt(updatedAt)}
          </p>
        ) : null}
      </div>

      {isFloorPersona ? (
        <>
          {/* #431 — first login only: the kennel-tech blurb from their invite
              email, echoed on the screen the email pointed at. */}
          <WelcomeCard config={config} />

          {/* #427 W1a — punch in from the phone right on arrival (story S1,
              P7 hands-first). */}
          <TimeClockCard
            config={config}
            primaryDemoted={clockStatus !== 'clocked_out'}
            onStatusChange={setClockStatus}
          />
        </>
      ) : null}

      {/* Date nav */}
      <div className="ferro-date-nav" data-testid="kennel-date-nav">
        <button type="button" className="ferro-btn ferro-btn--outline ferro-btn--sm" onClick={prev} aria-label="Previous day" data-testid="kennel-prev">‹</button>
        <span className="ferro-date-nav__label" data-testid="kennel-date-label">{dateLabel}</span>
        <button type="button" className="ferro-btn ferro-btn--outline ferro-btn--sm" onClick={next} aria-label="Next day" data-testid="kennel-next">›</button>
        <button type="button" className="ferro-btn ferro-btn--ghost ferro-btn--sm" onClick={() => setDate(todayIsoDate())}>Today</button>
      </div>

      {hasRoster && shown.length > 0 ? (
        <p className="ferro-muted ferro-kennel__source" data-testid="kennel-source-note">
          {SOURCE_NOTE}
        </p>
      ) : null}

      {/* Vaccine warnings */}
      {(expiredCount > 0 || unverifiedCount > 0) && !isLoading && (
        <div className="ferro-alert ferro-alert--warning" role="alert">
          {expiredCount > 0 && <span>{expiredCount} dog{expiredCount !== 1 ? 's' : ''} with expired vaccines on site. </span>}
          {unverifiedCount > 0 && <span>{unverifiedCount} dog{unverifiedCount !== 1 ? 's' : ''} with unverified records.</span>}
        </div>
      )}

      {feedSuccess && (
        <div className="ferro-alert ferro-alert--success" role="status">{feedSuccess}</div>
      )}

      {actionError && (
        <div className="ferro-alert ferro-alert--error ferro-alert--dismissible" role="alert">
          <span>{actionError}</span>
          <button type="button" className="ferro-alert__dismiss" onClick={() => setActionError(null)}>Dismiss</button>
        </div>
      )}

      {isLoading && !hasRoster && (
        <div className="ferro-skeleton-stack" data-testid="kennel-skeleton">
          {[1, 2, 3].map((i) => <div key={i} className="ferro-skeleton ferro-skeleton--stat" />)}
        </div>
      )}

      {error && !isLoading && (
        <div className="ferro-alert ferro-alert--error" role="alert" data-testid="kennel-error">
          <span>{error}</span>
          <button type="button" className="ferro-btn ferro-btn--sm ferro-btn--outline" style={{ marginLeft: '12px' }} onClick={() => void load()}>Retry</button>
        </div>
      )}

      {hasRoster && shown.length === 0 && (
        <div className="ferro-empty-card" data-testid="kennel-empty">
          <div className="ferro-empty-card__icon">🏠</div>
          <p>No dogs checked in for {dateLabel}.</p>
          <p className="ferro-muted">Dogs show here once they are checked in at the front desk.</p>
        </div>
      )}

      <div className="ferro-roster" data-testid="kennel-roster">
        {shown.map((animal) => (
          <div key={animal.bookingId} className="ferro-roster-card" data-testid={`kennel-card-${animal.dogId}`}>
            <div className="ferro-roster-card__header">
              <div>
                <h3 className="ferro-roster-card__name">{animal.dogName}</h3>
                <p className="ferro-roster-card__meta">
                  {animal.breed ?? 'Dog'}{animal.sizeCategory ? ` · ${animal.sizeCategory}` : ''} · {animal.customerFirstName} {animal.customerLastName}
                </p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                <span className="ferro-roster-card__flags" data-testid={`kennel-flags-${animal.dogId}`}>
                  <DogFlagChips flags={animal.flags} scope="dog" testIdPrefix={`kennel-${animal.dogId}-`} />
                </span>
                {animal.runAssignment
                  ? <span className="ferro-run-badge">{animal.runAssignment}</span>
                  : <span className="ferro-run-badge ferro-run-badge--unassigned">No run</span>
                }
                {/* Sawyer #1295 — the first "Ate none" meal of the day. */}
                <AteNoneBadge ateNone={animal.ateNone} dogName={animal.dogName} testId={`kennel-ate-none-${animal.dogId}`} />
                {/* #721 — the refusal streak, server-computed with the
                    roster. Renders nothing when there is no run, and not
                    when the "Ate none" badge already names that meal. */}
                {streakRepeatsAteNone(animal.careStreakSlot, parseAteNone(animal.ateNone)) ? null : (
                  <CareStreakBadge
                    label={animal.careStreakLabel}
                    days={animal.careStreakDays}
                    dogName={animal.dogName}
                    testId={`kennel-streak-${animal.dogId}`}
                  />
                )}
              </div>
            </div>

            {animal.behaviorNotes && (
              <p className="ferro-roster-card__meta" style={{ fontStyle: 'italic' }}>⚠ {animal.behaviorNotes}</p>
            )}
            <CareLines animal={animal} canOpenDog={rolesCanAccessPath(roles, `/dogs/${animal.dogId}`)} />

            {/* Run assignment input */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
              <input
                type="text"
                placeholder={animal.runAssignment ? `Change run (${animal.runAssignment})` : 'Assign run / kennel…'}
                value={runEdits[animal.bookingId] ?? ''}
                onChange={(e) => setRunEdits((prev) => ({ ...prev, [animal.bookingId]: e.target.value }))}
                style={{ border: '1.5px solid rgba(47,62,70,0.2)', borderRadius: '8px', padding: '8px 12px', fontSize: '0.9rem', fontFamily: 'var(--brand-font-body)', width: '180px' }}
                aria-label={`Run assignment for ${animal.dogName}`}
              />
              <button
                type="button"
                className="ferro-btn ferro-btn--sm ferro-btn--outline"
                disabled={!runEdits[animal.bookingId]?.trim() || pendingId === `run-${animal.bookingId}`}
                onClick={() => void handleAssignRun(animal)}
              >
                {pendingId === `run-${animal.bookingId}` ? '…' : 'Assign'}
              </button>
            </div>

            {/* Meal log — #721. Tap the meal, then say what happened. The
                second tap is the feature: without an outcome the log cannot
                answer "has he eaten?", which is the whole reason this
                exists. */}
            <div className="ferro-roster-card__actions">
              <span className="ferro-muted" style={{ fontSize: '0.82rem', alignSelf: 'center' }}>Log meal:</span>
              {MEALS.map((meal) => {
                const key = `${animal.dogId}-${meal}`;
                const open = openMeal === key;
                return (
                  <button
                    key={meal}
                    type="button"
                    className={`ferro-btn ferro-btn--sm ${open ? 'ferro-btn--outline' : 'ferro-btn--ghost'}`}
                    disabled={pendingId === key}
                    aria-expanded={open}
                    onClick={() => setOpenMeal(open ? null : key)}
                    aria-label={`Log ${MEAL_LABEL[meal]} for ${animal.dogName}`}
                    data-testid={`kennel-meal-${animal.dogId}-${meal}`}
                  >
                    {pendingId === key ? '…' : MEAL_LABEL[meal]}
                  </button>
                );
              })}
            </div>

            {openMeal?.startsWith(`${animal.dogId}-`) && (
              <div
                className="ferro-roster-card__actions"
                role="group"
                aria-label={`What happened at ${animal.dogName}'s meal?`}
                data-testid={`kennel-outcome-row-${animal.dogId}`}
              >
                <span className="ferro-muted" style={{ fontSize: '0.82rem', alignSelf: 'center' }}>
                  What happened?
                </span>
                {OUTCOME_BUTTONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className="ferro-btn ferro-btn--sm ferro-btn--outline"
                    disabled={pendingId !== null}
                    onClick={() =>
                      void handleLogFeeding(
                        animal,
                        openMeal.slice(animal.dogId.length + 1) as MealType,
                        o.value,
                      )
                    }
                    data-testid={`kennel-outcome-${animal.dogId}-${o.value}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Sawyer 02 kennel-stopgap (A1-stopgap) — the dog's PROFILE feeding and medication text, labelled
 * as profile text (refuter C1). Feeding always renders ("not on file" beats a
 * blank). A Meds line renders for med text; a dog with structured meds only
 * (the registry's "meds" flag, no text) gets a pointer to its profile instead
 * of silence (refuter C7). No text and no flag ⇒ no Meds line, which is not a
 * claim of "no meds".
 */
function CareLines({
  animal,
  canOpenDog,
}: {
  animal: OnSiteAnimal;
  canOpenDog: boolean;
}): JSX.Element {
  const feeding = careText(animal.feedingSchedule);
  const meds = careText(animal.medicationNotes);
  const structuredMedsOnly =
    meds === null && (animal.flags ?? []).some((f) => f.key === 'meds');
  return (
    <>
      <p className="ferro-roster-card__care" data-testid={`kennel-feeding-${animal.dogId}`}>
        <span className="ferro-roster-card__care-label">Feeding (profile):</span>{' '}
        {feeding ?? <span className="ferro-muted">not on file</span>}
      </p>
      {meds !== null ? (
        <p className="ferro-roster-card__care" data-testid={`kennel-meds-${animal.dogId}`}>
          <span className="ferro-roster-card__care-label">Meds (profile):</span> {meds}
        </p>
      ) : structuredMedsOnly ? (
        <p className="ferro-roster-card__care" data-testid={`kennel-meds-${animal.dogId}`}>
          <span className="ferro-roster-card__care-label">Meds (profile):</span>{' '}
          {canOpenDog ? (
            <Link className="ferro-roster-card__care-link" to={`/dogs/${animal.dogId}`}>
              on file, see the dog&apos;s profile
            </Link>
          ) : (
            <span className="ferro-muted">on file, see the dog&apos;s profile</span>
          )}
        </p>
      ) : null}
    </>
  );
}
